import React, { Fragment, FunctionComponent, useState, useContext, useEffect } from 'react';

import { Text } from '../../../shared/Text';
import { StepperContent, StepperHeader, StepperSubmit } from '../../../shared/Stepper';
import { useForm } from 'react-hook-form';
import { formItems, getPostData } from '../formItems';
import { FormContext } from '../../../../store/context/formContext';
import {
    MortgageTypeCard,
    MortgageTypeCardProps,
    MortgageTypeCardWrapper,
} from '../../../shared/MortgageTypeCard';
import { Input } from '../../../shared/Input';
import { InputHolder, InputHolderItem } from '../../../shared/InputHolder';
import { API } from '../../../../services';
import { Radio } from '../../../shared/Radio';
import ReactTooltip from 'react-tooltip';
import { assetPrefix } from '../../../../utils/assetPrefix';
import { getMortgageProviders } from '../../../../services/calculationsApi';
import { Select, SelectOption } from '../../../shared/Select';
import { Button } from '../../../shared/Button';
import { dotsToComma } from '../../../../services/helpers';
const PeriodOptions: SelectOption[] = [
    {
        name: '5 jaar',
        value: 5,
    },
    {
        name: '10 jaar',
        value: 10,
    },
    {
        name: '15 jaar',
        value: 15,
    },
    {
        name: '20 jaar',
        value: 20,
    },
    {
        name: '30 jaar',
        value: 30,
    },
];
const mortgageTypeCards: MortgageTypeCardProps[] = [
    {
        label: 'Annuïteiten',
        description:
            'Een annuïteitenhypotheek is een hypotheek waarbij het totale maandbedrag (bestaande uit rente en aflossing) gedurende de rentevaste periode gelijk blijft.',
        name: formItems.mortgageType,
        id: formItems.mortgageType,
        checked: true,
        defaultValue: '4',
    },
    {
        label: 'Lineair',
        description:
            'Een lineaire hypotheek is een hypotheek waarbij je gedurende de looptijd elke maand hetzelfde bedrag aflost. De rente is in het begin het hoogst en loopt gedurende de looptijd af. Je start dus met een hoog maandbedrag.',
        name: formItems.mortgageType,
        id: formItems.mortgageType,
        defaultValue: '9',
    },
];

export const Step2: FunctionComponent<{ id: number }> = ({ id }) => {
    const {
        register,
        setValue,
        handleSubmit,
        formState: { errors, isValid },
    } = useForm({ mode: 'onBlur' });

    const [completedText, setCompletedText] = useState('');
    const [changeinterestSettings, setChangeInterestSettings] = useState(false);

    const {
        updateForm,
        form,
        isNhg,
        updateData,
        setIsLoading,
        setIsNhg,
        setCalculatedMortgageType,
        setServerError,
        selectedPeriod,
        setSelectedPeriod,
        selectedInterest,
        selectedMortgageType,
        setSelectedMortgageType,
        setSelectedInterest,
    } = useContext(FormContext);

    useEffect(() => {
        const getInterestRate = async (
            mortgageType: number,
            isNhg: boolean,
            RentevastePeriode: number,
        ) => {
            const mortgageTypeFormat =
                mortgageType === 4 ? 'Annuïteitenhypotheek' : 'Lineaire_hypotheek';
            const riskClass = isNhg === true ? 0 : 100;
            const providers = await getMortgageProviders(
                mortgageTypeFormat,
                false,
                riskClass,
                RentevastePeriode,
            );
            return providers.producten[0].rentestand;
        };
        getInterestRate(selectedMortgageType, isNhg, selectedPeriod).then((interest) => {
            setSelectedInterest(interest);
            setValue('interest', interest);
        });

        if (isNhg) {
            setValue('isNhg', 'true');
        } else {
            setValue('isNhg', 'false');
        }
    }, [
        form,
        isNhg,
        selectedInterest,
        selectedMortgageType,
        selectedPeriod,
        setSelectedInterest,
        setIsNhg,
        setValue,
    ]);

    const onSubmit = (data: any) => {
        setCalculatedMortgageType(parseInt(data.mortgageType));
        updateForm(data);
        setServerError(false);
        setIsLoading(true);

        const formData = {
            ...form,
            ...data,
        };

        API.post<any>('calculator/maximummortgageformonthlypayment', getPostData(formData))
            .then((res) => {
                updateData({
                    ...res,
                });
                setIsLoading(false);
                setCompletedText(
                    mortgageTypeCards.filter(
                        (type) => type.defaultValue === data[formItems.mortgageType],
                    )[0].label,
                );
            })
            .catch((error) => {
                setServerError(true);
                updateData({
                    maximumMortgageForMonthlyPayment: 0,
                });
                setIsLoading(false);
            })
            .finally(() => {
                window.scrollTo({
                    top: 0,
                    behavior: 'smooth',
                });
            });
    };

    return (
        <Fragment>
            <StepperHeader title="Hypotheekvorm" id={id} completedText={completedText} />

            <StepperContent>
                <Text as="h3">Kies je hypotheekvorm</Text>

                <form onSubmit={handleSubmit(onSubmit)}>
                    <MortgageTypeCardWrapper>
                        {mortgageTypeCards.map((mortgageType, key) => (
                            <MortgageTypeCard
                                key={key}
                                label={mortgageType.label}
                                description={mortgageType.description}
                                name={mortgageType.name}
                                id={`${mortgageType.name}-${key}`}
                                register={register}
                                checked={mortgageType.checked}
                                defaultValue={mortgageType.defaultValue}
                                onClick={(value) => setSelectedMortgageType(parseInt(value))}
                            />
                        ))}
                    </MortgageTypeCardWrapper>
                    <hr />
                    <div className="u-margin-top--sm u-margin-bottom--md">
                        <Text as="h3">Nationale Hypotheek Garantie</Text>
                        <p>
                            Wil je de hypotheek berekenen met of zonder Nationale Hypotheek Garantie*
                            <img
                                data-tip="De <a href='https://www.hypotheker.nl/begrippenlijst/huis-kopen/nationale-hypotheek-garantie/'>Nationale Hypotheek Garantie (NHG)</a> is een garantie op hypotheken tot 355.000 euro. <br /> Sluit je een NHG-hypotheek af, dan leen je verantwoord en betaalbaar. <br /> Met een NHG-hypotheek loop je als woningeigenaar minder risico en profiteer je van een <a href='https://www.hypotheker.nl/begrippenlijst/hypotheek-afsluiten/rentekorting-hypotheek/'>rentekorting</a>."
                                src={`${assetPrefix}/assets/svg/info.svg`}
                                alt="info"
                            />
                        </p>
                        <ReactTooltip
                            place={'right'}
                            effect={'solid'}
                            data-iscapture="true"
                            clickable={true}
                            textColor="white"
                            html={true}
                        />
                        <InputHolder>
                            <InputHolderItem isSmall={true}>
                                <Radio
                                    label="Met NHG"
                                    checked={isNhg}
                                    name={formItems.isNhg}
                                    id={`${formItems.isNhg}-1`}
                                    defaultValue={'true'}
                                    register={register}
                                />
                            </InputHolderItem>
                            <InputHolderItem isSmall={true}>
                                <Radio
                                    label="Zonder NHG"
                                    name={formItems.isNhg}
                                    id={`${formItems.isNhg}-2`}
                                    defaultValue={'false'}
                                    register={register}
                                    validation={{
                                        onChange: (e: any) =>
                                            setIsNhg(e.currentTarget.value === 'true'),
                                    }}
                                />
                            </InputHolderItem>
                        </InputHolder>
                    </div>
                    <hr />
                    <Text as="h3">Adviesrente en rentevaste periode</Text>
                    <p>
                        De rentevaste periode is de afgesproken periode dat de rente gelijk blijft.
                        Het rentepercentage voor jouw hypotheek kun je voor verschillende rentevaste
                        perioden vastzetten. Dit kan variëren van een maand tot 30 jaar. <br />
                        <a
                            href="https://www.hypotheker.nl/begrippenlijst/hypotheek-afsluiten/rentevaste-periode/"
                            // eslint-disable-next-line react/jsx-no-target-blank
                            target="_blank">
                            Leef meer over rentevaste periode
                        </a>
                    </p>
                    {!changeinterestSettings ? (
                        <>
                            <div className="u-flex u-flex-between">
                                <div>
                                    <div className="u-h4">
                                        <b>Rente van {dotsToComma(selectedInterest)}%</b>
                                    </div>
                                    <div>Looptijd van 30 Jaar</div>
                                    <div>Rentevaste periode van {selectedPeriod} Jaar</div>
                                </div>
                                {/* eslint-disable-next-line jsx-a11y/anchor-is-valid  */}
                                <Button
                                    variant="ghost"
                                    label="Aanpassen"
                                    onClick={() => setChangeInterestSettings(true)}></Button>
                            </div>
                            <Input
                                type="hidden"
                                id={formItems.interest}
                                name={formItems.interest}
                                register={register}
                                hasError={errors.interest}
                                defaultValue={selectedInterest}
                            />
                            <Input
                                type="hidden"
                                id={formItems.period}
                                name={formItems.period}
                                register={register}
                                defaultValue={selectedPeriod}
                            />
                        </>
                    ) : (
                        <InputHolder>
                            <InputHolderItem>
                                <Select
                                    label="Rentevaste periode"
                                    options={PeriodOptions}
                                    id={formItems.period}
                                    name={formItems.period}
                                    register={register}
                                    defaultOption="10"
                                    validation={{
                                        required: true,
                                        onChange: (e: any) =>
                                            setSelectedPeriod(e.currentTarget.value),
                                    }}
                                />
                                {errors[formItems.period] && (
                                    <span className="form__error">Vul een geldige looptijd in</span>
                                )}
                            </InputHolderItem>
                            <InputHolderItem>
                                <Button
                                    extraClasses="u-float--right"
                                    variant="ghost"
                                    label="Opslaan"
                                    onClick={() => setChangeInterestSettings(false)}></Button>
                            </InputHolderItem>
                        </InputHolder>
                    )}

                    <StepperSubmit id={id} isValidated={isValid} variant="primary" />
                </form>
            </StepperContent>
        </Fragment>
    );
};
